import type { StandardButtonProps } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard } from '@eika/card';
import { Checkbox, type CheckboxProps } from '@eika/checkbox';
import { useTranslation } from '@translations';
import type { ReactNode } from 'react';
import { DocumentConsentProvider, useDocumentConsentContext } from './context';
import './document-consent.scss';
import { useDataAttributes } from '@data-attributes/use-data-attributes';
import { ErrorMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import type { ErrorResponse } from '@eika-infrastruktur/request';
import { useLinks } from '@hooks/use-links';
import { getPrettyErrorMessage } from '@utils/error-utils';
import { Documents } from './documents/documents';

interface DocumentConsentProps {
    children: ReactNode;
}

export const DocumentConsent = ({ children }: DocumentConsentProps) => {
    const { processSummary } = useTranslation();

    return (
        <DocumentConsentProvider>
            <div className="document-consent-card">
                <InfoCard.TitleH2>{processSummary.documentConsent.header}</InfoCard.TitleH2>
                <div className="document-consent-card__elements">{children}</div>
            </div>
        </DocumentConsentProvider>
    );
};

DocumentConsent.Documents = () => <Documents />;

DocumentConsent.ConfirmCheckBox = ({ onValueChange, ...props }: CheckboxProps) => {
    const { processSummary } = useTranslation();

    const { setSubmitWithoutConsentError, submitWithoutConsentError, setConsentHasBeenGiven } =
        useDocumentConsentContext();

    const onCheckConsent = (checked: boolean, value: string) => {
        setConsentHasBeenGiven(checked);

        if (checked) {
            setSubmitWithoutConsentError(undefined);
        }

        if (onValueChange) {
            onValueChange(checked, value);
        }
    };

    return (
        <Checkbox
            {...props}
            onValueChange={onCheckConsent}
            label={processSummary.documentConsent.confirmationText}
            error={submitWithoutConsentError}
            data-testid="confirm-checkbox"
            required
        />
    );
};

interface FormErrorProps extends StandardButtonProps {
    formError: string | undefined;
}

DocumentConsent.FormError = ({ formError }: FormErrorProps) => {
    if (!formError) {
        return null;
    }

    return <ErrorMessage text={formError} icon={IconTypes.LUKK_24} />;
};

interface SubmitErrorProps {
    error?: ErrorResponse | null;
}

DocumentConsent.SubmitError = ({ error }: SubmitErrorProps) => {
    const { processSummary, backToOverview } = useTranslation();
    const { overview } = useLinks();

    if (!error) {
        return null;
    }

    const errorMessage = getPrettyErrorMessage(error, processSummary.postProcessErrorDescription);

    return (
        <ErrorMessage
            className="document-consent-card__error-message"
            header={processSummary.postProcessError}
            text={errorMessage}
            icon={IconTypes.LUKK_24}
            clickableItem={<Link href={overview.url}>{backToOverview}</Link>}
        />
    );
};

interface SubmitButtonProps {
    buttonText: string;
    onSubmit: () => void;
    showSpinner?: boolean;
    showSuccess?: boolean;
}

DocumentConsent.SubmitButton = ({ buttonText, onSubmit, ...props }: SubmitButtonProps) => {
    const { consentHasBeenGiven, setSubmitWithoutConsentError } = useDocumentConsentContext();
    const { processSummary } = useTranslation();
    const { documentConsentDataAttributes } = useDataAttributes();

    const handleOnClick = () => {
        if (!consentHasBeenGiven) {
            setSubmitWithoutConsentError(processSummary.documentConsent.youMustConfirmBeforeProceeding);
            return;
        }

        setSubmitWithoutConsentError(undefined);
        onSubmit();
    };

    return (
        <ButtonWithSpinner
            className="document-consent-card__elements__submit-button"
            onClick={handleOnClick}
            {...documentConsentDataAttributes.submitButton}
            {...props}
        >
            {buttonText}
        </ButtonWithSpinner>
    );
};
